import type { Position, SortOption, VesselSizeInfo } from '@/types/positionList.types';

/**
 * Format date string to readable format
 */
export function formatPositionDate(dateString: string): string {
  if (!dateString || dateString.trim() === '' || dateString.trim() === ' ') {
    return 'N/A';
  }

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString; // Return original if can't parse
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
}

/**
 * Format date with time
 */
export function formatPositionDateTime(dateString: string): string {
  if (!dateString || dateString.trim() === '' || dateString.trim() === ' ') {
    return 'N/A';
  }

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
}

/**
 * Format DWT number with thousand separators
 */
export function formatDWT(dwt: string | number | null): string {
  if (dwt == null) return 'N/A';
  const dwtNum = typeof dwt === 'string' ? parseInt(dwt, 10) : dwt;
  if (isNaN(dwtNum)) return 'N/A';
  return dwtNum.toLocaleString('en-US');
}

/**
 * Get vessel size category information
 */
export function getVesselSizeInfo(vesselSize: number): VesselSizeInfo {
  switch (vesselSize) {
    case 2:
      return { name: 'VLCC', color: 'primary' };
    case 1:
      return { name: 'Suezmax', color: 'success' };
    case 0:
      return { name: 'Aframax', color: 'warning' };
    default:
      return { name: 'Unknown', color: 'default' };
  }
}

/**
 * Calculate days until next dry dock
 */
export function getDaysUntilDryDock(nextDD: string): number | null {
  if (!nextDD || nextDD.trim() === '') return null;

  try {
    const ddDate = new Date(nextDD);
    if (isNaN(ddDate.getTime())) return null;

    const now = new Date();
    const diffTime = ddDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  } catch {
    return null;
  }
}

/**
 * Get dry dock warning status
 */
export function getDryDockStatus(nextDD: string): {
  status: 'critical' | 'warning' | 'normal' | 'unknown';
  message: string;
} {
  const days = getDaysUntilDryDock(nextDD);

  if (days === null) {
    return { status: 'unknown', message: 'N/A' };
  }

  if (days < 0) {
    return { status: 'critical', message: 'Overdue' };
  } else if (days <= 180) {
    return { status: 'critical', message: `${days} days` };
  } else if (days <= 365) {
    return { status: 'warning', message: `${days} days` };
  } else {
    return { status: 'normal', message: formatPositionDate(nextDD) };
  }
}

/**
 * Get relative time string (e.g., "2 days ago")
 */
export function getRelativeTime(days: number): string {
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
}

/**
 * Sort positions based on selected option
 */
export function sortPositions(positions: Position[], sortBy: SortOption): Position[] {
  const sorted = [...positions];

  switch (sortBy) {
    case 'date-desc':
      return sorted.sort(
        (a, b) => new Date(b.positionListDate).getTime() - new Date(a.positionListDate).getTime()
      );

    case 'date-asc':
      return sorted.sort(
        (a, b) => new Date(a.positionListDate).getTime() - new Date(b.positionListDate).getTime()
      );

    case 'name-asc':
      return sorted.sort((a, b) => a.vesselName.localeCompare(b.vesselName));

    case 'name-desc':
      return sorted.sort((a, b) => b.vesselName.localeCompare(a.vesselName));

    case 'dwt-desc':
      return sorted.sort((a, b) => {
        const dwtA = a.dwt ? parseInt(a.dwt, 10) : 0;
        const dwtB = b.dwt ? parseInt(b.dwt, 10) : 0;
        return dwtB - dwtA;
      });

    case 'dwt-asc':
      return sorted.sort((a, b) => {
        const dwtA = a.dwt ? parseInt(a.dwt, 10) : 0;
        const dwtB = b.dwt ? parseInt(b.dwt, 10) : 0;
        return dwtA - dwtB;
      });

    case 'nextdd-asc':
      return sorted.sort((a, b) => {
        const dateA = new Date(a.nextDD).getTime();
        const dateB = new Date(b.nextDD).getTime();
        return dateA - dateB;
      });

    case 'lastupdate-desc':
      return sorted.sort((a, b) => b.daysSinceLastPositionUpdate - a.daysSinceLastPositionUpdate);

    default:
      return sorted;
  }
}

/**
 * Filter positions by search query
 */
export function filterPositions(positions: Position[], searchQuery: string): Position[] {
  if (!searchQuery.trim()) return positions;

  const query = searchQuery.toLowerCase();

  return positions.filter(
    (position) =>
      position.vesselName.toLowerCase().includes(query) ||
      position.lastPosition.toLowerCase().includes(query) ||
      position.built.includes(query) ||
      position.dwt?.includes(query) ||
      position.comment?.toLowerCase().includes(query)
  );
}

/**
 * Check if position has any warnings
 */
export function hasWarnings(position: Position): boolean {
  return (
    position.nextDryDockWarning === 1 ||
    position.sireWarning === 1 ||
    position.ageWarning === 1 ||
    position.narrowLayCanWarning === 1 ||
    position.onObservationList === 1 ||
    position.onRestrictionList === 1
  );
}

/**
 * Get all area dates for a position
 */
export function getAreaDates(position: Position): Array<{ area: string; date: string }> {
  return [
    { area: 'AG', date: position.ag },
    { area: 'WAF', date: position.waf },
    { area: 'Med', date: position.med },
    { area: 'NSea', date: position.nSea },
    { area: 'Ven', date: position.ven },
    { area: 'USG', date: position.usg },
  ].filter((item) => item.date && item.date.trim() !== '' && item.date.trim() !== ' ');
}

/**
 * Truncate long text with ellipsis
 */
export function truncateText(text: string, maxLength: number): string {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * Convert RTF content into readable plain text.
 * Falls back to the original trimmed text when the input is not RTF.
 */
export function parseRtfToPlainText(value: string | null | undefined): string {
  if (!value) return '';

  const trimmedValue = value.trim();
  if (!trimmedValue) return '';

  const isRtf = /^\{\\rtf/i.test(trimmedValue);
  if (!isRtf) return trimmedValue;

  const destinationControlWords = new Set([
    'fonttbl',
    'colortbl',
    'stylesheet',
    'info',
    'pict',
    'object',
    'footer',
    'header',
  ]);

  let parsedOutput = '';
  let currentIndex = 0;
  let isCurrentGroupIgnorable = false;
  const groupIgnorableStack: boolean[] = [];

  while (currentIndex < trimmedValue.length) {
    const currentChar = trimmedValue[currentIndex];

    if (currentChar === '{') {
      groupIgnorableStack.push(isCurrentGroupIgnorable);
      currentIndex += 1;
      continue;
    }

    if (currentChar === '}') {
      isCurrentGroupIgnorable = groupIgnorableStack.pop() ?? false;
      currentIndex += 1;
      continue;
    }

    if (currentChar !== '\\') {
      if (!isCurrentGroupIgnorable) {
        parsedOutput += currentChar;
      }
      currentIndex += 1;
      continue;
    }

    currentIndex += 1;
    if (currentIndex >= trimmedValue.length) {
      break;
    }

    const controlToken = trimmedValue[currentIndex];

    if (controlToken === '\\' || controlToken === '{' || controlToken === '}') {
      if (!isCurrentGroupIgnorable) {
        parsedOutput += controlToken;
      }
      currentIndex += 1;
      continue;
    }

    if (controlToken === '*') {
      isCurrentGroupIgnorable = true;
      currentIndex += 1;
      continue;
    }

    if (controlToken === "'") {
      const hexCode = trimmedValue.slice(currentIndex + 1, currentIndex + 3);
      if (/^[0-9a-fA-F]{2}$/.test(hexCode) && !isCurrentGroupIgnorable) {
        parsedOutput += String.fromCharCode(parseInt(hexCode, 16));
      }
      currentIndex += 3;
      continue;
    }

    let controlWord = '';
    while (currentIndex < trimmedValue.length && /[a-zA-Z]/.test(trimmedValue[currentIndex])) {
      controlWord += trimmedValue[currentIndex];
      currentIndex += 1;
    }

    let numberSign = 1;
    if (trimmedValue[currentIndex] === '-' || trimmedValue[currentIndex] === '+') {
      numberSign = trimmedValue[currentIndex] === '-' ? -1 : 1;
      currentIndex += 1;
    }

    let numericValueText = '';
    while (currentIndex < trimmedValue.length && /\d/.test(trimmedValue[currentIndex])) {
      numericValueText += trimmedValue[currentIndex];
      currentIndex += 1;
    }

    const numericValue = numericValueText ? numberSign * parseInt(numericValueText, 10) : null;

    if (trimmedValue[currentIndex] === ' ') {
      currentIndex += 1;
    }

    const normalizedControlWord = controlWord.toLowerCase();

    if (destinationControlWords.has(normalizedControlWord)) {
      isCurrentGroupIgnorable = true;
      continue;
    }

    if (isCurrentGroupIgnorable) {
      if (
        normalizedControlWord === 'u' &&
        currentIndex < trimmedValue.length &&
        !['\\', '{', '}'].includes(trimmedValue[currentIndex])
      ) {
        currentIndex += 1;
      }
      continue;
    }

    if (normalizedControlWord === 'par' || normalizedControlWord === 'line') {
      parsedOutput += '\n';
      continue;
    }

    if (normalizedControlWord === 'tab') {
      parsedOutput += '\t';
      continue;
    }

    if (normalizedControlWord === 'u' && numericValue !== null) {
      const unicodeCodePoint = numericValue < 0 ? 65536 + numericValue : numericValue;
      try {
        parsedOutput += String.fromCodePoint(unicodeCodePoint);
      } catch {
        // Ignore malformed unicode control words
      }

      if (
        currentIndex < trimmedValue.length &&
        !['\\', '{', '}'].includes(trimmedValue[currentIndex])
      ) {
        currentIndex += 1;
      }
    }
  }

  const normalizedText = parsedOutput
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return normalizedText;
}

/**
 * Get status badge variant
 */
export function getStatusBadgeVariant(
  status: 'onSubs' | 'scrubber' | 'restriction' | 'observation' | 'warning'
): 'success' | 'primary' | 'error' | 'warning' {
  switch (status) {
    case 'onSubs':
      return 'success';
    case 'scrubber':
      return 'primary';
    case 'restriction':
      return 'error';
    case 'observation':
      return 'warning';
    case 'warning':
      return 'error';
    default:
      return 'primary';
  }
}

/**
 * Format date for details page
 * Alias for formatPositionDate
 */
export function formatDate(dateString: string): string {
  return formatPositionDate(dateString);
}

/**
 * Format date in dd/MM/yyyy format (matching Angular pattern)
 */
export function formatDateDDMMYYYY(dateString: string | null | undefined): string {
  if (!dateString || dateString.trim() === '' || dateString.trim() === ' ') {
    return '';
  }

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return dateString;
  }
}

/**
 * Format number with specified decimal places (matching Angular number pipe pattern)
 * @param value - The number to format
 * @param minDecimals - Minimum decimal places
 * @param maxDecimals - Maximum decimal places
 */
export function formatNumber(
  value: number | string | null | undefined,
  minDecimals: number = 0,
  maxDecimals: number = 0
): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) {
    return '';
  }

  return num.toLocaleString('en-US', {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
  });
}

/**
 * Format speed value with 1 decimal place
 * Note: Returns just the formatted number, caller should add 'mts' suffix if needed
 */
export function formatSpeed(speed: number | string | null | undefined): string {
  const formatted = formatNumber(speed, 1, 1);
  return formatted || '0.0';
}

/**
 * Format DWT value with no decimals and thousand separators
 */
export function formatDWTValue(dwt: number | string | null | undefined): string {
  const formatted = formatNumber(dwt, 0, 0);
  return formatted || '0';
}

/**
 * Format pool points with 1 decimal place
 */
export function formatPoolPoints(points: number | string | null | undefined): string {
  const formatted = formatNumber(points, 1, 1);
  return formatted || '0';
}

/**
 * Get activity badge variant based on activity status
 */
export function getActivityBadgeVariant(
  activity: string | null
): 'success' | 'primary' | 'warning' | 'error' | 'default' {
  if (!activity) return 'default';

  const activityLower = activity.toLowerCase().trim();

  // On Subs or Fixed
  if (activityLower.includes('subs') || activityLower.includes('fixed')) {
    return 'success';
  }

  // Hold or Wait
  if (activityLower.includes('hold') || activityLower.includes('wait')) {
    return 'warning';
  }

  // Ballast or Available
  if (
    activityLower.includes('ballast') ||
    activityLower.includes('available') ||
    activityLower.includes('open')
  ) {
    return 'primary';
  }

  // Delay or Issue
  if (activityLower.includes('delay') || activityLower.includes('issue')) {
    return 'error';
  }

  return 'default';
}

/**
 * Get all status badges for a position
 */
export function getStatusBadges(
  position: Position
): Array<{ label: string; variant: 'success' | 'primary' | 'error' | 'warning' }> {
  const badges: Array<{ label: string; variant: 'success' | 'primary' | 'error' | 'warning' }> = [];

  // On Subs
  if (position.bitOnSubs === 1) {
    badges.push({ label: 'On Subs', variant: 'success' });
  }

  // Scrubber
  if (position.hasScrubber === 'Yes') {
    badges.push({ label: 'Scrubber', variant: 'primary' });
  }

  // Trading Restrictions
  if (position.onRestrictionList === 1 || position.tradingRestrictions) {
    badges.push({ label: 'Trading Restrictions', variant: 'error' });
  }

  // Observation List
  if (position.onObservationList === 1) {
    badges.push({ label: 'On Observation List', variant: 'warning' });
  }

  // Warnings
  if (position.nextDryDockWarning === 1) {
    badges.push({ label: 'DD Warning', variant: 'warning' });
  }

  if (position.sireWarning === 1) {
    badges.push({ label: 'SIRE Warning', variant: 'warning' });
  }

  if (position.ageWarning === 1) {
    badges.push({ label: 'Age Warning', variant: 'warning' });
  }

  if (position.narrowLayCanWarning === 1) {
    badges.push({ label: 'Narrow LayCan', variant: 'warning' });
  }

  return badges;
}
